import type { MeddraTerm } from '@/lib/api/generated/casedb';
import { CATALOG_RENDERER_NAMES } from '@/fwk.generated/field-catalog/catalogRendererNames';

export const CODING_RENDERER = CATALOG_RENDERER_NAMES.MEDDRA_CODING;
/** The catalog key of every MedDRA coding field: events, indications, medical history. */
export const CODING_FIELD_KEY = 'meddra_coding';
/** A field that stores a coding record (selection, status, history) rather than a plain value. */
export function isCodingField(fieldId: string): boolean {
  return fieldId.endsWith(`.${CODING_FIELD_KEY}`);
}
export const CODING_ACTION = { select: 'select', manual: 'manual', confirm: 'confirm', clear: 'clear' } as const;
export const CODING_STATUS_LABELS: Record<string, string> = {
  automatic: 'Automatically coded · Review pending', confirmed: 'Confirmed', manual: 'Manually entered · Unverified',
  cleared: 'Cleared by reviewer', needs_review: 'Needs review', failed: 'Coding failed', no_match: 'No match',
};
export interface CodingHistory {
  action: string; actor?: string; at?: string; before?: CodingState; after?: CodingState;
}
export interface CodingState {
  revision?: string; status?: string; version?: string; language?: string; lltCode?: string;
  pathId?: string; verbatim?: string; reason?: string; method?: string; term?: MeddraTerm;
  raw?: Record<string, string>; history?: CodingHistory[];
  action?: string; baseRevision?: string;
}
/** A stable identity for a history entry: the revision it produced, else when and what. */
export function codingHistoryKey(entry: CodingHistory): string {
  return entry.after?.revision ?? `${entry.at ?? ''}:${entry.action}`;
}
export function parseCoding(value: unknown): CodingState {
  if (value == null || value === '') return {};
  if (typeof value !== 'string') throw new Error('Invalid coding value: expected text');
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Invalid coding decision');
  return parsed as CodingState;
}
export function codingCommand(state: CodingState, verbatim: string, action: string, values: Partial<CodingState> = {}): CodingState {
  return { action, baseRevision: state.baseRevision ?? state.revision ?? '', verbatim, ...values };
}

/** Fields owned by one coding control; reported wording is never a coding projection. */
export function codingFields(fieldId: string) {
  if (fieldId.includes('.medical_history.')) return { source: 'disease_term', term: 'disease_coded_term', code: 'event_code' };
  if (fieldId.includes('.indications.')) return { source: 'indication', term: 'llt', code: 'code' };
  return { source: 'reported_term', term: 'lower_level_term', code: 'lower_level_term_code' };
}
